// react
import React, { useRef } from 'react';
import { useLocation, useMatches, useOutlet } from 'react-router-dom';

// hooks
import { useNavigationDirection } from '@/pwa/hooks/useNavigationDirection';

// framer-motion
import { AnimatePresence, motion, type Variants } from 'framer-motion';

// mui
import { Box, Stack } from '@mui/material';

const stackTransition = { duration: 0.25, ease: 'easeInOut' };

/**
 * Direction-aware variants for stacked page overlay transitions.
 *
 * Forward (PUSH/REPLACE): entering slides from +25% right, exiting slides to -25% left
 * Back (POP): entering slides from -25% left, exiting slides to +25% right
 */
const stackVariants: Variants = {
  initial: (isBack: boolean) => ({
    x: isBack ? '-25%' : '25%',
    opacity: 0,
  }),
  animate: {
    x: 0,
    opacity: 1,
  },
  exit: (isBack: boolean) => ({
    x: isBack ? '25%' : '-25%',
    opacity: 0,
    // When going back the exiting page must stack above the entering page
    // (otherwise the entering page slides in on top, looking like forward).
    zIndex: isBack ? 1300 : 1100,
  }),
};

/**
 * FrozenOutlet captures the outlet content at mount time so that
 * the exiting page renders its last state during the exit animation.
 */
const FrozenOutlet = ({ outlet }: { outlet: React.ReactNode }) => {
  const frozenRef = useRef(outlet);
  return <>{frozenRef.current}</>;
};

export interface StackLayoutProps {
  /** Render function that wraps the root (non-stacked) outlet content. */
  children: (outlet: React.ReactNode) => React.ReactNode;
  /** When true, freeze root outlet when rootStack routes are active (default true). */
  freezeOnRootStack?: boolean;
}

/**
 * StackLayout provides direction-aware slide + opacity transitions for
 * routes marked with `handle: { stack: true }`.
 *
 * Root content slides left and fades when a stacked page enters.
 * The stacked page renders as a position-absolute overlay with slide transitions.
 * Root-to-root navigation uses a fade transition.
 *
 * The `children` render function receives the animated root outlet and should
 * wrap it with any chrome (e.g. NavDrawer, sidebar).
 */
export const StackLayout = ({
  children,
  freezeOnRootStack = true,
}: StackLayoutProps) => {
  const currentOutlet = useOutlet();
  const location = useLocation();
  const matches = useMatches();

  // detect child stacked routes (show overlay)
  const isStacked = matches.some(
    (m) => (m.handle as { stack?: boolean } | undefined)?.stack,
  );

  // detect root-level stacked routes rendered by Root
  const isRootStacked =
    freezeOnRootStack &&
    matches.some(
      (m) => (m.handle as { rootStack?: boolean } | undefined)?.rootStack,
    );

  const isAnyStacked = isStacked || isRootStacked;

  const isBack = useNavigationDirection();

  // keep a frozen copy of the last root outlet and its pathname to show
  // underneath the stack overlay. Using the same AnimatePresence key when
  // isStacked toggles prevents the root content from unmounting/remounting
  // (which would reset component state like the active Tabs selection).
  const lastRootOutletRef = useRef<React.ReactNode>(null);
  const lastRootPathnameRef = useRef(location.pathname);
  if (!isAnyStacked && currentOutlet) {
    lastRootOutletRef.current = currentOutlet;
    lastRootPathnameRef.current = location.pathname;
  }

  const rootOutlet = (
    <AnimatePresence mode='wait' initial={false}>
      <motion.div
        key={isAnyStacked ? lastRootPathnameRef.current : location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{ height: '100%' }}
      >
        {isAnyStacked ? lastRootOutletRef.current : currentOutlet}
      </motion.div>
    </AnimatePresence>
  );

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
      }}
    >
      <motion.div
        initial={false}
        animate={{
          x: isStacked ? '-25%' : '0%',
          opacity: isStacked ? 0 : 1,
        }}
        transition={stackTransition}
        style={{ height: '100%' }}
      >
        {children(rootOutlet)}
      </motion.div>
      <AnimatePresence initial={false} custom={isBack}>
        {isStacked && (
          <motion.div
            key={location.pathname}
            custom={isBack}
            variants={stackVariants}
            initial='initial'
            animate='animate'
            exit='exit'
            transition={stackTransition}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1200,
              overflow: 'hidden',
              backgroundColor: 'var(--mui-palette-background-default, #fff)',
            }}
          >
            <FrozenOutlet outlet={currentOutlet} />
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
};

StackLayout.displayName = 'StackLayout';
export default StackLayout;
